import { MapBrowserEvent } from "ol";
import { Coordinate, equals } from "ol/coordinate";
import Target from "ol/events/Target";
import Feature from "ol/Feature"
import Point from "ol/geom/Point"
import VectorSource from "ol/source/Vector";
import DeleteTrackEvent from "./DeleteTrackEvent";
import InfoMessageEvent from "./InfoMessageEvent";
import PreUndoEvent from "./PreUndoEvent";
import { Renderer } from "./Renderer";
import { TrackEntity } from "./TrackEntity";
import AnchorPointFeature from "./AnchorPointFeature"
import UserEventInfo from "./UserEventInfo";
import _ from 'lodash'
import SetCurrentTrackData from "./SetCurrentTrackData";
import HomeEvents from "./HomeEvents";
import PointFeatureSource from "./PointFeatureSource";
import { PointFeatureSubclass } from "./PointFeature";
import { LineString } from "ol/geom";

export interface JoinInfo {
    event: UserEventInfo|undefined;
    coord: Coordinate;
    trackToJoin: TrackEntity|undefined;
}

export default class JoinHelper {
    joinIndicatorFeatureSource: VectorSource<Point>;
    private featureToJoin: AnchorPointFeature|undefined
    private joinIndicator: PointFeatureSubclass|undefined
    private renderer: Renderer<any>
    private homeEvents: HomeEvents

    constructor(joinIndicatorFeatureSource: VectorSource<Point>, homeEvents: HomeEvents, renderer: Renderer<any>) {
        this.joinIndicatorFeatureSource = joinIndicatorFeatureSource
        this.renderer = renderer
        this.homeEvents = homeEvents
    }

    unsetFeatureToJoin() {
        if(this.joinIndicator) {
            this.joinIndicatorFeatureSource.removeFeature(this.joinIndicator)
        }
        this.joinIndicator = undefined
        this.featureToJoin = undefined
    }

    setFeatureToJoin(featureToJoin: AnchorPointFeature) {
        const featureCoords = featureToJoin.getGeometry().getCoordinates()
        if(!this.joinIndicator) {
            this.joinIndicator = this.createJoinIndicator(featureCoords)
            this.joinIndicatorFeatureSource.addFeature(this.joinIndicator)
        } else {
            this.joinIndicator.getGeometry().setCoordinates(featureCoords)
        }
        this.featureToJoin = featureToJoin
    }

    createJoinIndicator(coordinate: Coordinate) {
        const feature = new PointFeatureSubclass(new Point(coordinate))
        return feature
    }

    getJoinInfo(event: UserEventInfo): JoinInfo {
        if(this.featureToJoin === undefined) {
            return {
                event: event,
                coord: event.coordinate,
                trackToJoin: undefined
            }
        } else {
            return {
                event: undefined,
                coord: this.featureToJoin.getGeometry().getCoordinates(),
                trackToJoin: this.featureToJoin.track
            }
        }
    }

    joinTracks(firstTrack: TrackEntity, secondTrack: TrackEntity, allowMismatch = false): TrackEntity {
        // firstTrack becomes the only track. We delete secondTrack, after adding its points to firstTrack.
        // sanity check:
        const firstTrackLastPoint = _.last(firstTrack.getDots())
        const secondTrackFirstPoint = _.first(secondTrack.getDots())

        if(firstTrackLastPoint === undefined || secondTrackFirstPoint === undefined) {
            console.error('Cannot join tracks, as firstTrackLastPoint and secondTrackFirstPoint are undefined or different ', firstTrackLastPoint, secondTrackFirstPoint)
            return firstTrack
        } else {
            let needToExtendFirst = false
            if(!equals(firstTrackLastPoint.getGeometry().getCoordinates(), secondTrackFirstPoint.getGeometry().getCoordinates())) {
                if(!allowMismatch) {
                    console.error('Cannot join tracks, as firstTrackLastPoint and secondTrackFirstPoint are different ', firstTrackLastPoint, secondTrackFirstPoint)
                    return firstTrack
                } else {
                    needToExtendFirst = true
                }
            }
            const reinstateSecondTrackData = secondTrack.getReinstateData(this.renderer)
            const restoreFirstTrackData = firstTrack.getRestoreData(this.renderer)
            const setCurrentTrackData = new SetCurrentTrackData() // reroute doesn't change the current track when we join, but draw does. This sets it back to what it was.
            this.homeEvents.handlePreUndoEvent(new PreUndoEvent([restoreFirstTrackData, reinstateSecondTrackData, setCurrentTrackData], 'join tracks'))

            if(needToExtendFirst) {
                firstTrack.addSegment(new LineString([firstTrackLastPoint.getGeometry().getCoordinates(), secondTrackFirstPoint.getGeometry().getCoordinates()]))
            }
            firstTrack.addDots(secondTrack.getDots().slice(1)) // all but the first point
            firstTrack.addSegments(secondTrack.getSegments())
            this.homeEvents.handleDeleteTrackEvent(new DeleteTrackEvent(secondTrack.value, false))
            this.homeEvents.handleInfoMessageEvent(new InfoMessageEvent(`Joined\n${firstTrack.name} to\n${secondTrack.name}.\n(If you did not mean to, click 'Undo' once.)`))
            this.unsetFeatureToJoin()
            return firstTrack
        }
    }

}